<template>
  <div class="PersonCard relative flex flex-col items-stretch">
    <div
      v-if="showActiveIndicator && person.isActive"
      class="text-white font-bold rounded-md w-fit h-12 -mb-3 pb-2 px-2 self-center flex flex-row items-center gap-2"
      :class="{
        'bg-success': person.isActive,
        '!bg-gray-400': !person.isActive,
      }"
    >
      <IconComponent
        icon="circle"
        class="-mr-1 inline"
        :class="{
          blink: person.isActive,
          'text-gray-300': !person.isActive,
        }"
      />
      <span class="uppercase">
        {{
          person.isActive
            ? $t("personProfile.openToOffers")
            : $t("personProfile.notOpenToOffers")
        }}
      </span>
    </div>
    <div
      ref="card"
      class="bg-white rounded-md shadow-md w-full h-full min-h-[250px]"
      @click="goToPersonProfile"
    >
      <div
        class="flex flex-col cursor-pointer p-2 gap-2 justify-between h-full"
      >
        <div class="flex gap-2 flex-1">
          <div
            class="flex-shrink-0 w-20 md:w-36 min-h-full overflow-hidden relative isolate rounded-md"
          >
            <PersonPictureSet
              :person="person"
              :alt="person.name"
              class="picture"
            />
            <div v-if="isVisited" class="is-visited-badge">
              <IconComponent icon="eye" />
              {{ $t("personCard.visited") }}
            </div>
          </div>
          <div class="flex flex-col items-stretch justify-between flex-1 gap-2">
            <div>
              <NuxtLink
                class="font-bold text-primary"
                :to="professionalLink"
                target="_blank"
                :rel="`${person.excluded ? 'nofollow' : ''}`"
                @click.native.prevent="
                  $trackers.trackClick('PersonCardName', trackingData)
                "
              >
                {{ person.name }}
              </NuxtLink>
              <div
                v-if="professions.length"
                class="flex flex-wrap text-xs items-center"
              >
                <IconComponent icon="briefcase" class="mr-1" />
                <p
                  v-for="(prof, index) in professions"
                  :key="`profession-${prof.id}`"
                  class="font-bold mr-1 first-letter:capitalize"
                >
                  {{ prof.name }}
                  {{ index < professions.length - 1 ? " · " : "" }}
                </p>
              </div>
            </div>
            <div
              v-if="person.memo"
              class="p-2 text-[0.625rem] md:text-xs rounded-md bg-gray-100 flex flex-col flex-1 gap-3"
            >
              <div
                class="overflow-hidden text-ellipsis line-clamp-4"
                style="word-break: break-word"
              >
                {{ person.memo.slice(0, 500) }}
              </div>
              <div class="flex-1 flex items-end justify-end">
                <NuxtLink
                  class="bg-info hover:bg-info-darker rounded-md p-1 text-xs font-bold !text-white"
                  :to="professionalLink"
                  target="_blank"
                  :rel="`${person.excluded ? 'nofollow' : ''}`"
                  @click.prevent
                >
                  {{ $t("personCard.more") }}
                </NuxtLink>
              </div>
            </div>
            <div
              v-if="isOnlineProfession"
              class="text-xs flex flex-col gap-1"
            >
              <span class="flex flex-row items-center gap-1">
                <IconComponent icon="globe" class="w-4 shrink-0 text-primary-light" />
                {{ $t("personProfile.location.onlineCoverage.cardOnline") }}
              </span>
              <span
                v-if="showInPersonConsultation && personCity"
                class="flex flex-row items-center gap-1"
              >
                <IconComponent icon="map-marker-alt" class="w-4 shrink-0 text-red-500" />
                {{
                  $t("personProfile.location.onlineCoverage.cardInPerson", {
                    city: personCity,
                  })
                }}
              </span>
            </div>
            <div v-else class="text-xs flex flex-row items-center gap-1">
              <template v-if="highlightedCity">
                <IconComponent icon="check" class="text-success" />
                <span>{{
                  $t("workThere.text", { cityName: highlightedCity.name })
                }}</span>
              </template>
              <template v-else>
                <IconComponent icon="map-marker-alt" />
                <span v-if="showGoToHouseKm && person.goToHouse">
                  <template v-if="goToWholeCountry">
                    {{ $t("personProfile.location.everywhere") }}
                  </template>
                  <template v-else>
                    <i18n path="personProfile.location.goToHouse">
                      <template #city>
                        <span class="font-bold">
                          {{ personCity }}
                        </span>
                      </template>
                      <template #km>
                        <span class="font-bold">
                          {{ person.goToHouseKm }}
                        </span>
                      </template>
                    </i18n>
                  </template>
                </span>
                <span v-else>{{ personCity }}</span>
              </template>
            </div>
          </div>
          <div
            class="min-w-[100px] flex flex-col items-end justify-between text-[0.625rem] md:text-xs"
          >
            <div class="flex flex-col items-end gap-1">
              <FavouriteButton
                :entity-type="FAVOURITE_ENTITY_TYPES.PROFESSIONAL"
                :entity-id="person.id"
                button-class="mr-1 !text-xl"
                @change="onFavouriteChange"
              />

              <RatingIndicator :score="person.score" />

              <PersonBadge
                v-for="badge in personLabels"
                :key="`badge-${badge.name}`"
                :name="badge.name"
                :description="badge.description"
                class="font-bold w-max mb-1"
              />
            </div>

            <div
              v-if="person.priceByProfessionTask"
              class="text-right font-bold text-gray-500"
            >
              <p class="font-semibold text-xs first-letter:capitalize">
                {{ person.priceByProfessionTask.professionTask.name }}
              </p>
              <p class="price">
                {{ formatTaskPrice(person.priceByProfessionTask) }}
              </p>
            </div>

            <div class="flex flex-col gap-0 text-right font-semibold">
              <div
                v-if="winCount > 0"
                class="flex flex-row gap-1 justify-end items-center"
              >
                {{
                  $t("personCard.comission", {
                    count: winCount,
                  })
                }}
                <IconComponent icon="check" class="text-success w-5" />
              </div>
              <div
                v-if="person.scoreCount > 0"
                class="flex flex-row gap-1 justify-end items-center"
              >
                {{ $t("personCard.opinion", { count: person.scoreCount }) }}
                <IconComponent icon="star" class="text-rating w-5" />
              </div>
              <div
                v-if="person.answerCount > 0"
                class="flex flex-row gap-1 justify-end items-center"
              >
                {{ $t("personCard.answers", { count: person.answerCount }) }}
                <IconComponent icon="comments" class="text-primary w-5" />
              </div>
              <div
                v-if="photoCount > 0"
                class="flex flex-row gap-1 justify-end items-center"
              >
                {{ $t("personCard.referencePhotos", { count: photoCount }) }}
                <IconComponent icon="camera" class="text-primary-darkest w-5" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col md:flex-row gap-2">
          <slot name="buttons">
            <JoszakiButton
              v-if="person.isActive && phoneNumber?.number"
              class="h-9 text-sm flex-1"
              type="info"
              icon-left="phone-alt"
              :inverted="true"
              :outline="true"
              @click.prevent.stop="phoneNumberClicked"
            >
              {{ $helpers.formatPhoneNumber(phoneNumber) }}
            </JoszakiButton>
            <JoszakiButton
              class="h-9 text-sm flex-1"
              type="info"
              icon-left="pencil-alt"
              :inverted="true"
              :outline="true"
              @click.prevent.stop="buttonClicked"
            >
              {{ $t(`requestTender.fromPerson`) }}
            </JoszakiButton>
          </slot>
        </div>
      </div>
    </div>
    <div
      v-if="showBoxTender"
      class="min-h-full absolute bg-whitesmoke rounded-sm z-50 left-0 right-0 top-[calc(100%+34px)]"
      @click.stop
    >
      <BoxTender
        class="h-full"
        :show-close="true"
        :person="person"
        @close="showBoxTender = false"
      />
    </div>
  </div>
</template>

<script>
import PersonBadge from "../_refactored/profile/PersonBadge.vue";
import RatingIndicator from "~/components/_refactored/rating/RatingIndicator.vue";
import PersonPictureSet from "~/components/PersonPictureSet.vue";
import JoszakiButton from "~/components/_refactored/common/JoszakiButton.vue";
import BoxTender from "~/components/_refactored/app/boxes/BoxTender.vue";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { useTaskPriceFormatters } from "~/composables/taskPriceFormatters";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";
import IS_PHONE_MISMATCH from "~/components/_refactored/card/isPhoneMismatch.graphql";
import { EVENT_OPEN_PERSON_PROFILE } from "~/composables/tracking/useTrackOpenPersonProfile";
import { EVENT_REQUEST_TENDER } from "~/composables/requestTender";
import {
  isOnlineProfessionEnabled,
  isInPersonConsultationVisible,
} from "~/experiments/onlineProfession";

export default {
  components: {
    PersonPictureSet,
    RatingIndicator,
    PersonBadge,
    JoszakiButton,
    BoxTender,
    FavouriteButton,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    highlightedCity: {
      type: Object,
      default: null,
    },
    showGoToHouseKm: {
      type: Boolean,
      default: false,
    },
    online: {
      type: Boolean,
      default: false,
    },
    professionTaskName: {
      type: String,
      default: "",
    },
    showActiveIndicator: {
      type: Boolean,
      default: false,
    },
    highlightedProfession: {
      type: Object,
      default: null,
    },
    highlightedProfessionTask: {
      type: Object,
      default: null,
    },
    profileLink: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { formatTaskPrice } = useTaskPriceFormatters();

    return {
      formatTaskPrice,
      FAVOURITE_ENTITY_TYPES,
    };
  },
  data() {
    return {
      isVisited: false,
      showBoxTender: false,
    };
  },
  computed: {
    highlightedProfessionTaskProfessionIds() {
      return (this.highlightedProfessionTask?.professions ?? []).map(
        (p) => p.id
      );
    },
    photoCount() {
      return this.person?.photoCount || this.person?.photos?.length || 0;
    },
    city() {
      return this.$store.getters["listPage/city"];
    },
    personCity() {
      return this.person.city?.name || this.person.city;
    },
    goToWholeCountry() {
      return this.person?.goToHouseKm > 500;
    },
    showInPersonConsultation() {
      // The "Személyes konzultáció: {city}" line is only shown in variant2.
      return isInPersonConsultationVisible(this.$ab);
    },
    isOnlineProfession() {
      // Gated behind the PostHog feature flag (variant1 or variant2).
      if (!isOnlineProfessionEnabled(this.$ab)) {
        return false;
      }
      // Explicit override from the parent (e.g. professionTask-level flag).
      if (this.online) {
        return true;
      }
      // Specific profession in context, when its flag is loaded.
      if (this.highlightedProfession && this.highlightedProfession.online != null) {
        return this.highlightedProfession.online;
      }
      // ProfessionTask context: online only if all its professions are online.
      const taskProfessions = this.highlightedProfessionTask?.professions ?? [];
      if (taskProfessions.some((p) => p.online != null)) {
        return taskProfessions.every((p) => p.online);
      }
      // Fallback: the person's own professions are all online.
      const professions = this.person?.professions ?? [];
      return professions.length > 0 && professions.every((p) => p.online);
    },
    highlightedProfessions() {
      return (this.person?.professions ?? []).filter(
        (p) =>
          p.id === this.highlightedProfession?.id ||
          this.highlightedProfessionTaskProfessionIds.includes(p.id)
      );
    },
    nonHighlightedProfessions() {
      return (this.person?.professions ?? []).filter(
        (p) =>
          p.id !== this.highlightedProfession?.id &&
          !this.highlightedProfessionTaskProfessionIds.includes(p.id)
      );
    },
    personLabels() {
      return [...(this.person?.labels ?? [])].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    professions() {
      if (!this.person.professions) {
        return [];
      }

      return [
        ...this.highlightedProfessions,
        ...this.nonHighlightedProfessions.slice(
          0,
          3 - this.highlightedProfessions.length
        ),
      ];
    },
    trackingData() {
      return {
        personId: this.person.id,
        isActive: this.person.isActive,
        score: this.person.score,
        scoreCount: this.person.scoreCount,
        winCount: this.winCount,
      };
    },
    winCount() {
      return this.person.winCount ?? 0;
    },
    phoneNumber() {
      return this.person?.phoneNumber ?? this.person?.publicPhoneNumber;
    },
    professionalLink() {
      if (this.profileLink) {
        return this.profileLink;
      }

      const hashParams = [];
      if (this.highlightedCity) {
        hashParams.push(`city=${this.highlightedCity.seoName}`);
      }

      if (this.highlightedProfession) {
        hashParams.push(`profession=${this.highlightedProfession.seoName}`);
      }

      const basePath = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: this.person.seoName,
        },
      });

      return hashParams.length > 0
        ? `${basePath}#${hashParams.join("&")}`
        : basePath;
    },
  },
  mounted() {
    this.isVisited = this.$store.getters["localStorage/isVisitedPerson"](
      this.person.id
    );
  },
  methods: {
    phoneNumberClicked() {
      this.$query(IS_PHONE_MISMATCH, {
        personId: this.person.id,
        phone: this.phoneNumber?.number,
      })
        .then((res) => {
          if (res.isPhoneMismatch) {
            this.$sentry.captureMessage(
              `Phone mismatch for person ${this.person.id}, phone: ${this.phoneNumber?.number} url: ${window.location.href}`,
              {
                level: "fatal",
              }
            );
          }
        })
        .catch((err) => {
          console.error(err, "Failed to query phone mismatch");
        });
      this.$trackers.trackEvent({
        title: "Clicked PersonCard Phone Number",
        data: this.trackingData,
      });
      this.$trackers.trackClick("PersonCardPhoneNumber", this.trackingData);
      this.$emit("callProfessional", this.person);
    },
    buttonClicked() {
      this.$trackers.trackEvent({
        title: "Clicked PersonCard Button",
        data: this.trackingData,
      });
      this.$trackers.trackClick("PersonCardButton", this.trackingData);

      this.goToTenderRequest();
    },
    goToPersonProfile() {
      this.$track.analyticsEvent({
        category: "szaki-kartya-v2",
        action: "kattintas-szakira",
      });
      this.$trackers.trackClick("PersonCard", this.trackingData);
      this.registerVisited();

      this.$mitt.emit(EVENT_OPEN_PERSON_PROFILE, this.person);
      this.$emit("select");
    },
    goToTenderRequest() {
      this.$track.analyticsEvent({
        category: "szaki-kartya-v2",
        action: "kattintas-ajanlatkeresre",
      });

      this.$mitt.emit(EVENT_REQUEST_TENDER, {
        profession: this.professions?.[0],
        person: this.person,
        source: "personCard",
      });
      this.$emit("requestTender", { profession: this.professions?.[0] });
    },

    onFavouriteChange({ favourited }) {
      this.$emit("like", {
        personId: this.person.id,
        liked: favourited,
      });
    },
    registerVisited() {
      this.isVisited = true;
      this.$store.dispatch("localStorage/visitPerson", this.person.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.picture::v-deep picture {
  width: 100%;
  height: 100%;
}

.picture::v-deep img {
  height: 100%;
  width: 100%;
  object-fit: cover;
}
</style>
